import type { ComponentType, SVGProps } from 'react';
import * as Flags from 'country-flag-icons/react/3x2';
import { hasFlag } from 'country-flag-icons';
import { cn } from '@/lib/cn';

type SvgFlag = ComponentType<SVGProps<SVGSVGElement>>;

export interface FlagProps {
  /** ISO 3166-1 alpha-2 country code. */
  code: string;
  className?: string;
  title?: string;
  /** Pixel height of the flag (width scales at 3:2). Default 14. */
  size?: number;
}

/** Renders an SVG country flag, or a compact initials badge when unknown. */
export function Flag({ code, className, title, size = 14 }: FlagProps) {
  const upper = (code || '').trim().toUpperCase();
  const label = title ?? upper;
  const Comp = upper && hasFlag(upper) ? (Flags as Record<string, SvgFlag>)[upper] : undefined;

  if (Comp) {
    return (
      <span title={label} className={cn('inline-flex shrink-0', className)}>
        <Comp
          className="rounded-[2px] shadow-[0_0_0_1px_rgb(17_27_45/0.08)]"
          style={{ height: size, width: (size * 3) / 2 }}
          aria-label={label}
        />
      </span>
    );
  }

  const initials = (upper || '??').slice(0, 2);
  return (
    <span
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-[2px] border border-line bg-surface-sunken font-mono text-[10px] font-semibold uppercase leading-none text-ink-secondary',
        className,
      )}
      style={{ height: size, width: (size * 3) / 2 }}
    >
      {initials}
    </span>
  );
}
